import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkPlus, Library } from 'lucide-react';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { aiImg2ImgPromptCaseAPI, fundingAuthAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { IMG2IMG_PROMPT_MAX_LENGTH } from '@/pages/ipe/workbench/constants/img2imgStudioConfig';
import { resolveDefaultTitlePresetScope } from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import { buildTeamOwnerNameMap } from '@/pages/ipe/workbench/utils/aiCopywritingTemplateLoader';
import { toIpeUserErrorMessage } from '@/pages/ipe/workbench/utils/errorHandler';
import {
  mapImg2ImgPromptCaseApiItem,
} from '@/pages/ipe/workbench/utils/img2imgPromptCaseLoader';
import { isTeamSpaceEntryAvailable } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import Img2ImgPromptCaseLibraryModal from '@/pages/ipe/workbench/components/Img2ImgPromptCaseLibraryModal';
import Img2ImgPromptCaseFormModal from '@/pages/ipe/workbench/modals/Img2ImgPromptCaseFormModal';
import IpeDeleteConfirmModal from '@/pages/ipe/workbench/modals/IpeDeleteConfirmModal';
import '@/styles/ipe/workbench/Img2ImgPage.css';

/** 万能改图：与 Img2ImgPage 侧栏可对齐项（prompt + 选用/新增指令模板；参考图在任务执行时提供） */
export default function Img2ImgStepParams({ p, onChange, readOnly = false }) {
  const { t } = useTranslation('workbench');
  const { user } = useIpeAuth();
  const workspaceMode = useIpeWorkspaceMode();
  const currentUserId = user?.id != null ? String(user.id) : (user?._id != null ? String(user._id) : '');

  const [canAccessTeamPresets, setCanAccessTeamPresets] = useState(false);
  const [teamOwnerNameByUserId, setTeamOwnerNameByUserId] = useState({});
  const [caseListNonce, setCaseListNonce] = useState(0);
  const [caseLibraryOpen, setCaseLibraryOpen] = useState(false);
  const [caseLibraryScope, setCaseLibraryScope] = useState(() => (
    resolveDefaultTitlePresetScope(workspaceMode, false)
  ));
  const [caseFormOpen, setCaseFormOpen] = useState(false);
  const [caseFormMode, setCaseFormMode] = useState('create');
  const [caseFormScope, setCaseFormScope] = useState('personal');
  const [caseFormEditing, setCaseFormEditing] = useState(null);
  const [caseFormDefaults, setCaseFormDefaults] = useState(null);
  const [caseFormKey, setCaseFormKey] = useState(0);
  const [caseFormSaving, setCaseFormSaving] = useState(false);
  const [caseDeleteTarget, setCaseDeleteTarget] = useState(null);
  const [caseDeleting, setCaseDeleting] = useState(false);

  const selectedCaseId = String(p.img2imgPromptCaseId || '').trim();

  useEffect(() => {
    let cancelled = false;
    fundingAuthAPI.getAccountCenter()
      .then(async (center) => {
        if (cancelled) return;
        const available = isTeamSpaceEntryAvailable(center);
        setCanAccessTeamPresets(available);
        setCaseLibraryScope((prev) => {
          const preferred = resolveDefaultTitlePresetScope(workspaceMode, available);
          if (prev === 'team' && !available) return preferred;
          return prev || preferred;
        });
        if (!available) {
          setTeamOwnerNameByUserId({});
          return;
        }
        try {
          const overview = await fundingAuthAPI.getTeamOverview();
          if (!cancelled) {
            setTeamOwnerNameByUserId(buildTeamOwnerNameMap(overview?.members));
          }
        } catch {
          if (!cancelled) setTeamOwnerNameByUserId({});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCanAccessTeamPresets(false);
          setTeamOwnerNameByUserId({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceMode]);

  const bumpCaseList = useCallback(() => {
    setCaseListNonce((n) => n + 1);
  }, []);

  const openCaseFormCreate = useCallback((scope, options = {}) => {
    setCaseLibraryOpen(false);
    setCaseFormMode('create');
    setCaseFormScope(scope === 'team' ? 'team' : 'personal');
    setCaseFormEditing(null);
    setCaseFormDefaults(options.prefillFromPrompt
      ? { prompt: String(p.prompt || '').trim() }
      : null);
    setCaseFormKey((k) => k + 1);
    setCaseFormOpen(true);
  }, [p.prompt]);

  const openCaseFormEdit = useCallback((item) => {
    if (!item?.canEdit) {
      showToast(t('studioToast.noPermEdit'), 'warning');
      return;
    }
    setCaseLibraryOpen(false);
    setCaseFormMode('edit');
    setCaseFormScope(item.scope === 'team' ? 'team' : 'personal');
    setCaseFormEditing(item);
    setCaseFormDefaults(null);
    setCaseFormKey((k) => k + 1);
    setCaseFormOpen(true);
  }, [t]);

  const openCaseDelete = useCallback((item) => {
    if (!item?.canDelete) {
      showToast(t('studioToast.noPermDelete'), 'warning');
      return;
    }
    setCaseDeleteTarget(item);
  }, [t]);

  const handleCaseDeleteConfirm = useCallback(async () => {
    const target = caseDeleteTarget;
    if (!target?.id) return;
    setCaseDeleting(true);
    try {
      await aiImg2ImgPromptCaseAPI.delete(target.id);
      showToast(t('studioToast.templateDeleted'), 'success');
      setCaseDeleteTarget(null);
      if (caseFormEditing && String(caseFormEditing.id) === String(target.id)) {
        setCaseFormOpen(false);
        setCaseFormEditing(null);
      }
      if (selectedCaseId === String(target.id)) {
        onChange({ img2imgPromptCaseId: '', img2imgPromptCaseScope: '' });
      }
      bumpCaseList();
    } catch (e) {
      showToast(toIpeUserErrorMessage(e, t('studioToast.templateDeleteFailed')), 'error');
    } finally {
      setCaseDeleting(false);
    }
  }, [caseDeleteTarget, caseFormEditing, selectedCaseId, onChange, bumpCaseList, t]);

  const handleCaseFormSubmit = useCallback(async (payload) => {
    setCaseFormSaving(true);
    try {
      let savedCase = null;
      if (caseFormMode === 'edit' && caseFormEditing?.id) {
        const { caseScope: _scope, ...updateBody } = payload;
        const res = await aiImg2ImgPromptCaseAPI.update(caseFormEditing.id, updateBody);
        savedCase = mapImg2ImgPromptCaseApiItem(res?.case);
        showToast(t('studioToast.templateSaved'), 'success');
      } else {
        const res = await aiImg2ImgPromptCaseAPI.create(payload);
        savedCase = mapImg2ImgPromptCaseApiItem(res?.case);
        showToast(t('studioToast.templateCreated'), 'success');
      }
      if (savedCase) {
        onChange({
          img2imgPromptCaseId: String(savedCase.id),
          img2imgPromptCaseScope: savedCase.scope,
          prompt: String(savedCase.prompt || '').slice(0, IMG2IMG_PROMPT_MAX_LENGTH),
        });
      }
      setCaseFormOpen(false);
      setCaseFormEditing(null);
      setCaseFormDefaults(null);
      if (payload.caseScope === 'personal' || payload.caseScope === 'team') {
        setCaseLibraryScope(payload.caseScope);
      }
      bumpCaseList();
    } catch (e) {
      showToast(toIpeUserErrorMessage(e, t('studioToast.templateSaveFailed')), 'error');
    } finally {
      setCaseFormSaving(false);
    }
  }, [caseFormMode, caseFormEditing, onChange, bumpCaseList, t]);

  const applyCase = useCallback((item) => {
    if (!item) return;
    onChange({
      img2imgPromptCaseId: String(item.id || ''),
      img2imgPromptCaseScope: item.scope || '',
      prompt: String(item.prompt || '').slice(0, IMG2IMG_PROMPT_MAX_LENGTH),
    });
    showToast(t('studioToast.templateApplied'), 'success');
  }, [onChange, t]);

  const openLibrary = () => {
    if (caseLibraryOpen) {
      setCaseLibraryOpen(false);
      return;
    }
    if (caseFormOpen) setCaseFormOpen(false);
    setCaseLibraryScope(
      resolveDefaultTitlePresetScope(workspaceMode, canAccessTeamPresets),
    );
    setCaseLibraryOpen(true);
  };

  const openCreate = () => {
    if (caseFormOpen && caseFormMode === 'create') {
      setCaseFormOpen(false);
      return;
    }
    openCaseFormCreate(
      resolveDefaultTitlePresetScope(workspaceMode, canAccessTeamPresets) === 'team'
        ? 'team'
        : 'personal',
      { prefillFromPrompt: true },
    );
  };

  return (
    <div className="img2img-page wf-studio-params-root wf-editor-img2img">
      {!readOnly ? (
        <div className="studio-case-actions wf-editor-img2img__case-actions">
          <button type="button" className="studio-case-btn" onClick={openLibrary}>
            <Library size={14} aria-hidden="true" />
            {t('workflowParams.pickTemplate')}
          </button>
          <button type="button" className="studio-case-btn" onClick={openCreate}>
            <BookmarkPlus size={14} aria-hidden="true" />
            {t('workflowParams.newTemplate')}
          </button>
        </div>
      ) : null}

      <section className="img2img-studio__section">
        <label className="img2img-studio__field-label" htmlFor="wf-img2img-prompt">
          {t('workflowParams.prompt')}
        </label>
        <div className="img2img-studio__prompt-wrap">
          <textarea
            id="wf-img2img-prompt"
            className="img2img-studio__prompt"
            value={p.prompt || ''}
            maxLength={IMG2IMG_PROMPT_MAX_LENGTH}
            disabled={readOnly}
            placeholder={t('workflowParams.promptPh')}
            onChange={(e) => onChange({ prompt: e.target.value.slice(0, IMG2IMG_PROMPT_MAX_LENGTH) })}
          />
          <span className="img2img-studio__prompt-count">
            {(p.prompt || '').length}/{IMG2IMG_PROMPT_MAX_LENGTH}
          </span>
        </div>
      </section>

      {selectedCaseId ? (
        <p className="wf-editor-params__muted">
          {t('workflowParams.boundPrompt', {
            scope: p.img2imgPromptCaseScope === 'personal'
              ? t('workflowParams.personal')
              : p.img2imgPromptCaseScope === 'team'
                ? t('workflowParams.team')
                : t('workflowParams.official'),
          })} ID：{selectedCaseId}
        </p>
      ) : null}

      <Img2ImgPromptCaseLibraryModal
        isOpen={caseLibraryOpen}
        onClose={() => setCaseLibraryOpen(false)}
        selectedId={selectedCaseId}
        initialScope={caseLibraryScope}
        canAccessTeamPresets={canAccessTeamPresets}
        currentUserId={currentUserId}
        ownerNameByUserId={teamOwnerNameByUserId}
        refreshNonce={caseListNonce}
        escapeDisabled={caseFormOpen || Boolean(caseDeleteTarget)}
        centered
        onSelect={applyCase}
        onRequestCreate={openCaseFormCreate}
        onRequestEdit={openCaseFormEdit}
        onRequestDelete={openCaseDelete}
      />
      <Img2ImgPromptCaseFormModal
        key={`wf-i2i-case-form-${caseFormKey}`}
        isOpen={caseFormOpen}
        mode={caseFormMode}
        initialScope={caseFormScope}
        caseItem={caseFormEditing}
        defaults={caseFormMode === 'create' ? caseFormDefaults : null}
        canAccessTeamPresets={canAccessTeamPresets}
        saving={caseFormSaving}
        centered
        onClose={() => {
          if (caseFormSaving) return;
          setCaseFormOpen(false);
          setCaseFormEditing(null);
          setCaseFormDefaults(null);
        }}
        onSubmit={handleCaseFormSubmit}
      />
      <IpeDeleteConfirmModal
        open={Boolean(caseDeleteTarget)}
        title={t('studioChrome.deleteTemplate')}
        confirming={caseDeleting}
        confirmDisabled={caseDeleting}
        onCancel={() => {
          if (caseDeleting) return;
          setCaseDeleteTarget(null);
        }}
        onConfirm={handleCaseDeleteConfirm}
      >
        {caseDeleteTarget
          ? t('studioChrome.deleteTemplateConfirm', { name: caseDeleteTarget.name })
          : ''}
      </IpeDeleteConfirmModal>
    </div>
  );
}
